/** The most dice a pool holds after a modifier adds dice to it (`modifiers: 'dice'`): with every die exploding ten times, its faces stay within what a roll record holds. */
export const MAX_POOL = 60;
/** How many dice are thrown: the count, or, when a modifier adds and removes dice, the count with it (none at least). */
export function poolSize(d, modifier) {
    return d.success?.modifiers === 'dice' ? Math.max(0, Math.min(MAX_POOL, d.count + modifier)) : d.count;
}
/** The modifier that is added to the result: none when it changed the pool's size instead. */
export const addedModifier = (d, modifier) => (d.success?.modifiers === 'dice' || d.success?.modifiers === 'target' ? 0 : modifier);
/** The highest TN a throw names: past what a die exploding its limit can reach, no die succeeds anyway. */
const MAX_TARGET = 10_000;
/**
 * A test's dice made concrete for one throw (§3.2.1), or null when the throw does not fit them: a rated count needs a
 * number of dice from 1 to `MAX_POOL`; a TN the throw gives needs a whole number (raised to `minTarget`), and dice
 * with a fixed mark, or none (an open test), take no TN. The Rule of Six's die explodes only when the TN is above its
 * sides, and then stops once its faces reach the TN; a keep the throw's dice cannot exceed is none.
 */
export function testDice(t, at) {
    const count = t.count === 'rated' ? at.dice : t.count;
    if (!Number.isInteger(count) || count < 1 || count > MAX_POOL)
        return null;
    const given = t.success?.atLeast === 'target';
    if (given ? !(Number.isInteger(at.target) && Math.abs(at.target) <= MAX_TARGET) : at.target !== null)
        return null;
    const s = t.success;
    const tn = s ? (s.atLeast === 'target' ? Math.max(s.minTarget ?? 1, at.target) : s.atLeast) : null;
    const out = { count, sides: t.sides };
    if (t.explode && !t.explode.aboveTarget)
        out.explode = { on: t.explode.on, limit: t.explode.limit };
    else if (t.explode && tn !== null && tn > t.sides)
        out.explode = { on: t.explode.on, limit: t.explode.limit, until: tn };
    if (t.keep && t.keep.n < count)
        out.keep = { ...t.keep };
    if (s && tn !== null)
        out.success = { atLeast: tn, modifiers: s.modifiers, ...(s.chains ? { chains: s.chains } : {}), ...(s.disaster ? { disaster: s.disaster } : {}) };
    return out;
}
/** Whether a die rolls again: it shows `on` or more, it is within its limit, and (the Rule of Six) it is short of the TN. */
const explodes = (d, faces) => Boolean(d.explode) && faces.length <= d.explode.limit && faces[faces.length - 1] >= d.explode.on
    && (d.explode.until === undefined || sum(faces) < d.explode.until);
/** Throw the dice: each die, then its explosions while it shows `on` or more, up to the limit; then keep. */
export function throwPool(d, rng, modifier = 0) {
    const out = [];
    for (let i = 0; i < poolSize(d, modifier); i += 1) {
        const faces = [rng(d.sides)];
        while (explodes(d, faces))
            faces.push(rng(d.sides));
        out.push({ faces });
    }
    return keepDice(d, out);
}
const sum = (xs) => xs.reduce((a, b) => a + b, 0);
/** Mark the dice keep drops: all but the highest (or lowest) `n` by value; none when the pool has `n` or fewer. */
export function keepDice(d, dice) {
    const plain = dice.map((x) => ({ faces: [...x.faces] }));
    if (!d.keep || plain.length <= d.keep.n)
        return plain;
    const order = plain.map((x, i) => ({ i, v: sum(x.faces) })).sort((a, b) => (d.keep.which === 'highest' ? b.v - a.v : a.v - b.v) || a.i - b.i);
    const kept = new Set(order.slice(0, d.keep.n).map((x) => x.i));
    return plain.map((x, i) => (kept.has(i) ? x : { ...x, dropped: true }));
}
/** The throw's natural result: the kept dice's number of successes, or their total. */
export function poolResult(d, dice) {
    const kept = dice.filter((x) => !x.dropped);
    if (d.success?.chains === 'sum')
        return kept.filter((x) => sum(x.faces) >= d.success.atLeast).length;
    if (d.success)
        return kept.reduce((n, x) => n + x.faces.filter((f) => f >= d.success.atLeast).length, 0);
    return sum(kept.map((x) => sum(x.faces)));
}
/** Whether the throw is a disaster (the Rule of One, p. 38): its dice name one, and every die thrown shows 1. */
export const poolDisaster = (d, dice) => d.success?.disaster === 'all_ones' && dice.length > 0 && dice.every((x) => x.faces[0] === 1);
/** Every face thrown, in order: the roll record's `dice`. */
export const poolFaces = (dice) => dice.flatMap((x) => x.faces);
/**
 * A stored pool against the dice it says it was thrown with: exact shape or null. The number of dice, each chain's
 * explosions (only on `on` or more, at most `limit`) and the dropped dice must be what these dice and this modifier make.
 */
export function parsePool(v, d, modifier) {
    if (!Array.isArray(v) || v.length !== poolSize(d, modifier))
        return null;
    const out = [];
    for (const x of v) {
        if (typeof x !== 'object' || x === null)
            return null;
        const { faces, dropped } = x;
        if (!Array.isArray(faces) || faces.length < 1 || faces.length > 1 + (d.explode?.limit ?? 0))
            return null;
        if (!faces.every((f) => Number.isInteger(f) && f >= 1 && f <= d.sides))
            return null;
        // Every face but the last exploded; the last stopped, unless the limit stopped it (or, by the Rule of Six, the TN).
        const chain = faces;
        if (chain.length > 1 && !d.explode)
            return null;
        if (chain.slice(0, -1).some((_, i) => !explodes(d, chain.slice(0, i + 1))))
            return null;
        if (explodes(d, chain))
            return null;
        if (dropped !== undefined && dropped !== true)
            return null;
        out.push({ faces: [...chain], ...(dropped ? { dropped: true } : {}) });
    }
    const again = keepDice(d, out);
    return again.every((x, i) => Boolean(x.dropped) === Boolean(out[i].dropped)) ? out : null;
}
/** "8, 3, 10→6, 9, [2]": each die's faces, an explosion by an arrow, a dropped die in brackets. */
export const poolText = (dice) => dice.map((x) => (x.dropped ? `[${x.faces.join('→')}]` : x.faces.join('→'))).join(', ');
/** How many throws a band may take before the server says it cannot be met. */
export const BAND_TRIES = 20_000;
/**
 * The main roll of a chain thrown, with the DM's band when one rides on it: the whole throw is made again until its
 * result lands in the band, so every result in it comes as often as the dice make it. Null when no throw landed (a band
 * the dice reach only by long odds); the caller refuses it in words. A band the dice cannot reach at all is the caller's
 * to refuse first.
 */
export function throwInBand(d, rng, modifier, band) {
    for (let i = 0; i < (band ? BAND_TRIES : 1); i += 1) {
        const dice = throwPool(d, rng, modifier);
        const r = poolResult(d, dice);
        if (!band || (r >= band.min && r <= band.max))
            return dice;
    }
    return null;
}
//# sourceMappingURL=pool.js.map